import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

/**
 * HoursGrid — coloană stânga = lista angajaților, fiecare zi a lunii are 2 inputs:
 *   ore lucrate (top) + ore conduse (bottom). Editare inline cu PUT /management/hours-grid/{pid}.
 * Afișează în header coloanele zilelor; sâmbăta amber, duminica neagră.
 */
export default function HoursGrid({ month: monthProp, holidaysByDate = {}, projects = [], regii = [] }) {
  const { canDo } = useAuth();
  const today = new Date();
  const [monthLocal, setMonth] = useState(monthProp || `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`);
  const month = monthProp || monthLocal;
  const [data, setData] = useState({ persons: [], grid: {} });
  const [editing, setEditing] = useState(null); // { person_id, date }

  const load = async () => {
    try {
      const { data: r } = await api.get(`/management/hours-grid?month=${month}`);
      setData(r);
    } catch { setData({ persons: [], grid: {} }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const [y, m] = month.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);

  const setCell = async (personId, dateStr, payload) => {
    try {
      await api.put(`/management/hours-grid/${personId}`, { date: dateStr, ...payload });
      // update local
      setData((prev) => {
        const grid = { ...(prev.grid || {}) };
        const personGrid = { ...(grid[personId] || {}) };
        personGrid[dateStr] = { ...(personGrid[dateStr] || {}), ...payload };
        grid[personId] = personGrid;
        return { ...prev, grid };
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    }
  };

  const totals = useMemo(() => {
    const t = { worked: 0, driven: 0, byPerson: {} };
    for (const p of data.persons) {
      let pw = 0, pd = 0;
      const pgrid = data.grid?.[p.id] || {};
      for (const d in pgrid) {
        pw += parseFloat(pgrid[d].hours || 0) || 0;
        pd += parseFloat(pgrid[d].hours_driven || 0) || 0;
      }
      t.byPerson[p.id] = { worked: pw, driven: pd };
      t.worked += pw;
      t.driven += pd;
    }
    return t;
  }, [data]);

  return (
    <div className="card-flat overflow-x-auto" data-testid="hours-grid-pane">
      {!monthProp && (
        <div className="p-3 border-b border-[var(--border)] flex items-end gap-3">
          <div>
            <Label className="small-label">Luna</Label>
            <Input type="month" value={monthLocal} onChange={(e) => setMonth(e.target.value)} className="mt-1 w-44"/>
          </div>
          <div className="text-xs text-zinc-500 ml-auto">
            <span className="mr-3">Total ore lucrate: <strong className="text-[var(--primary)]">{totals.worked.toFixed(1)}</strong></span>
            <span>Total ore conduse: <strong className="text-amber-600">{totals.driven.toFixed(1)}</strong></span>
          </div>
        </div>
      )}
      <table className="text-[11px] border-collapse min-w-full">
        <thead className="bg-zinc-50">
          <tr>
            <th className="sticky left-0 bg-zinc-50 p-2 text-left font-bold uppercase tracking-wider text-[10px] text-zinc-700 border-r-2 border-zinc-300 z-20 min-w-[160px]">
              Angajat
            </th>
            <th className="p-1 text-center text-[10px] font-bold text-zinc-600 border-r border-[var(--border)] min-w-[60px]">Total</th>
            {days.map((d) => {
              const dStr = `${month}-${String(d).padStart(2, "0")}`;
              const wd = new Date(y, m - 1, d).getDay();
              const isSunday = wd === 0;
              const isSaturday = wd === 6;
              const hol = holidaysByDate[dStr];
              const isNational = hol?.type === "national";
              const isCantonal = hol?.type === "cantonal";
              const black = isSunday || isNational;
              return (
                <th
                  key={d}
                  className={`p-0.5 border-b border-r border-[var(--border)] text-[10px] font-semibold min-w-[64px] ${
                    black ? "bg-zinc-900 text-white" :
                    isSaturday ? "bg-amber-50 text-amber-700" :
                    isCantonal ? "bg-purple-50 text-purple-800" : "text-zinc-700"
                  }`}
                  title={hol ? `${hol.name}${hol.cantons?.length ? ` (${hol.cantons.join(", ")})` : ""}` : dStr}
                >
                  {d}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {data.persons.length === 0 && (
            <tr><td colSpan={days.length + 2} className="p-8 text-center text-sm text-zinc-400 italic">Niciun angajat activ.</td></tr>
          )}
          {data.persons.map((p) => {
            const pgrid = data.grid?.[p.id] || {};
            const pTotal = totals.byPerson[p.id] || { worked: 0, driven: 0 };
            return (
              <tr key={p.id} className="border-b border-[var(--border)] hover:bg-zinc-50/40" data-testid={`hg-row-${p.id}`}>
                <td className="sticky left-0 bg-white p-2 border-r-2 border-zinc-300 z-10">
                  <div className="flex items-center gap-2">
                    {p.color && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: p.color }}/>}
                    <div className="min-w-0">
                      <div className="text-xs font-semibold truncate">{p.name}</div>
                      {p.intern_number && <div className="text-[9px] text-zinc-500">#{p.intern_number}</div>}
                    </div>
                  </div>
                </td>
                <td className="p-1 text-center border-r border-[var(--border)]">
                  <div className="font-bold text-[var(--primary)]">{pTotal.worked.toFixed(1)}h</div>
                  {pTotal.driven > 0 && <div className="text-[9px] text-amber-600">+ {pTotal.driven.toFixed(1)}h cond.</div>}
                </td>
                {days.map((d) => {
                  const dStr = `${month}-${String(d).padStart(2, "0")}`;
                  const cell = pgrid[dStr];
                  const wd = new Date(y, m - 1, d).getDay();
                  const isSunday = wd === 0;
                  const hol = holidaysByDate[dStr];
                  const black = isSunday || hol?.type === "national";
                  const isEditing = editing && editing.person_id === p.id && editing.date === dStr;
                  const canEdit = canDo("MANAGEMENT", "edit") && !black;
                  return (
                    <td
                      key={d}
                      onClick={() => canEdit && setEditing({ person_id: p.id, date: dStr })}
                      className={`p-0.5 border-r border-[var(--border)] text-center align-top cursor-pointer ${black ? "bg-zinc-900/95" : "hover:bg-[var(--primary-subtle)]/30"}`}
                      data-testid={`hg-cell-${p.id}-${d}`}
                    >
                      {isEditing ? (
                        <CellEditor
                          cell={cell}
                          projects={projects}
                          regii={regii}
                          onClose={() => setEditing(null)}
                          onSave={async (payload) => {
                            await setCell(p.id, dStr, payload);
                            setEditing(null);
                          }}
                        />
                      ) : (
                        <div className="text-[10px] leading-tight py-0.5">
                          {cell?.hours ? (
                            <div className="font-bold text-[var(--primary)]">{cell.hours}</div>
                          ) : <div className="text-zinc-300">·</div>}
                          {cell?.hours_driven ? (
                            <div className="text-amber-600 font-semibold">{cell.hours_driven}</div>
                          ) : null}
                          {cell?.color && (
                            <div className="w-full h-0.5 mt-0.5" style={{ background: cell.color }}/>
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CellEditor({ cell, projects, regii, onClose, onSave }) {
  const [hours, setHours] = useState(cell?.hours ?? "");
  const [hoursDriven, setHoursDriven] = useState(cell?.hours_driven ?? "");
  const [projectId, setProjectId] = useState(cell?.project_id || "__none__");

  const save = () => {
    onSave({
      hours: parseFloat(hours || 0) || 0,
      hours_driven: parseFloat(hoursDriven || 0) || 0,
      project_id: projectId === "__none__" ? null : projectId,
    });
  };

  return (
    <div className="bg-white border-2 border-[var(--primary)] rounded-md p-1.5 shadow-xl absolute z-50 min-w-[180px]" data-testid="hg-cell-editor">
      <Input
        type="number" step="0.5" min={0}
        autoFocus
        value={hours}
        onChange={(e) => setHours(e.target.value)}
        placeholder="lucrate"
        className="h-7 text-xs"
        data-testid="hg-hours"
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") onClose(); }}
      />
      <Input
        type="number" step="0.5" min={0}
        value={hoursDriven}
        onChange={(e) => setHoursDriven(e.target.value)}
        placeholder="conduse"
        className="h-7 text-xs mt-1"
        data-testid="hg-hours-driven"
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") onClose(); }}
      />
      <Select value={projectId} onValueChange={setProjectId}>
        <SelectTrigger className="h-6 text-[10px] mt-1" data-testid="hg-project"><SelectValue/></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">— Niciun proiect —</SelectItem>
          {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          {regii.length > 0 && <div className="text-[9px] text-zinc-400 px-2 pt-1 uppercase font-bold">Regii</div>}
          {regii.map((r) => <SelectItem key={r.id} value={r.id}>{r.name || r.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="flex gap-1 mt-1.5">
        <button onClick={save} className="flex-1 bg-[var(--primary)] text-white text-[10px] font-bold py-1 rounded hover:opacity-90" data-testid="hg-save">OK</button>
        <button onClick={onClose} className="px-2 text-[10px] text-zinc-500 hover:text-zinc-900">Esc</button>
      </div>
    </div>
  );
}
